'use strict'

import {useMemo} from 'react'
import {useCategory} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useBrand} from '../contexts/brand-context'
import {getRootCategoryId} from '../utils/brandUtils'
import {findUgcCategory} from '../utils/ugc-flags'
import {CAT_MENU_DEFAULT_NAV_SSR_DEPTH, UGC_CATEGORY_LOOKUP_DEPTH} from '../constants'

// Resolves the brand's UGC landing category (c_isUGC === true) from the nav tree
// so links can use its real c_relativeUrl instead of the hardcoded /ugc route.
// Reuses _app's cached nav-tree query; only does a deeper lookup if it misses.
export const useUgcCategory = ({enabled = true} = {}) => {
    const {site} = useMultiSite() || {}
    const {brandId} = useBrand() || {}
    const rootCategoryId = getRootCategoryId(site?.id, brandId)

    const {data: navCategoriesTree, isLoading: isNavTreeLoading} = useCategory({
        parameters: {id: rootCategoryId, levels: CAT_MENU_DEFAULT_NAV_SSR_DEPTH},
        enabled: enabled && Boolean(rootCategoryId)
    })
    const navUgcCategory = useMemo(() => findUgcCategory(navCategoriesTree), [navCategoriesTree])

    const shouldLookDeeper =
        enabled && Boolean(rootCategoryId) && !isNavTreeLoading && !navUgcCategory
    const {data: deepCategoriesTree, isLoading: isDeepLoading} = useCategory({
        parameters: {id: rootCategoryId, levels: UGC_CATEGORY_LOOKUP_DEPTH},
        enabled: shouldLookDeeper
    })

    const ugcCategory = useMemo(
        () => navUgcCategory || findUgcCategory(deepCategoriesTree) || null,
        [navUgcCategory, deepCategoriesTree]
    )

    return {
        ugcCategory,
        isLoading: Boolean(isNavTreeLoading) || (shouldLookDeeper && Boolean(isDeepLoading))
    }
}
